import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { useOdoRoles } from '../../hooks/useRoles';
import { getTeamColor } from '@odo/shared-ui/components/teamColors';
import { FLUID_LABEL_CLASS, FLUID_PILL_TIGHT_CLASS } from '../shared/fluidPill';

interface TeamSelectorProps {
    team: string | null;
    onSelect: (team: string) => void;
    size?: 'sm' | 'md';
    dropdownAlign?: 'left' | 'right';
    // Shrinks to a FLUID_CELL_CLASS table column, down to a colour swatch.
    fluid?: boolean;
}

const SIZE_CLASSES = {
    md: 'min-w-[100px] text-sm px-3.5 py-1.5',
    sm: 'min-w-[90px] text-xs px-3.5 py-1.5',
    fluid: `min-w-[min(90px,100%)] max-w-full h-7 text-xs px-3.5 py-1.5 ${FLUID_PILL_TIGHT_CLASS}`
};

const TeamSelector: React.FC<TeamSelectorProps> = ({
    team,
    onSelect,
    size = 'sm',
    dropdownAlign = 'right',
    fluid = false
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const { roles } = useOdoRoles();

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const currentTeam = team ?? 'Admin';
    const color = getTeamColor(currentTeam);
    const sizeClass = SIZE_CLASSES[fluid ? 'fluid' : size];

    return (
        <div ref={ref} className="relative">
            <Tooltip content={currentTeam} disabled={!fluid}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(!open);
                    }}
                    aria-label={fluid ? `Team: ${currentTeam}` : undefined}
                    className={`inline-flex items-center justify-center gap-1.5 font-semibold rounded-md transition-all duration-150 hover:brightness-90 active:scale-[0.97] ${sizeClass}`}
                    style={{ backgroundColor: color, color: '#fff' }}
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                    {fluid ? <span className={FLUID_LABEL_CLASS}>{currentTeam}</span> : currentTeam}
                    <ChevronDownIcon
                        className={`w-3 h-3 flex-shrink-0 opacity-60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    />
                </button>
            </Tooltip>

            {open && (
                <div
                    className={`absolute top-full mt-2 z-30 bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[220px] animate-[fadeSlideDown_120ms_ease-out] ${
                        dropdownAlign === 'left' ? 'left-0' : 'right-0'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col gap-1.5">
                        {roles.map((r) => {
                            const roleColor = getTeamColor(r.name);
                            const isActive = r.name === currentTeam;
                            return (
                                <button
                                    key={r.id}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(r.name);
                                        setOpen(false);
                                    }}
                                    className={`flex items-center justify-center w-full text-xs font-semibold py-[9px] px-3.5 rounded-lg text-white transition-all duration-100 ${
                                        isActive
                                            ? 'ring-2 ring-offset-1 ring-gray-800'
                                            : 'hover:brightness-110 hover:scale-[1.02]'
                                    }`}
                                    style={{ backgroundColor: roleColor }}
                                >
                                    {r.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamSelector;
